import { put, select } from "redux-saga/effects"
import { AnyAction } from "redux" // Added for type compatibility
import { getUserData } from "../selectors"
import { put as putFetch } from "../../utils/http"
import { createAction } from "../../utils/redux"
import { createSnackbarMessage } from "../../actions/ui/snackbar"
import MessageTypes from "../../enums/MessageTypes"
import ErrorCodes from "../../enums/errorCodes"

interface LockSagaParams<T = unknown> {
  resourcePath: string
  successAction: string
  failureAction: string
  successMessage: string
  failureMessage: string
  getTokenFromResponse: (response: T) => string
}

interface LockSagaAction {
  payload: string
  name?: string
}

interface UserData {
  token?: string
}

interface ApiError {
  errorCode?: string
  message?: string
  response?: {
    errorCode?: string
  }
}

const normalizeErrorCode = (error: ApiError): string | undefined => {
  const code =
    error?.errorCode ||
    error?.response?.errorCode ||
    error?.message

  if (typeof code === "string" && code.startsWith("Error: ")) {
    return code.replace(/^Error: /, "")
  }

  return code
}

const isDeviceLimitError = (
  errorCode?: string,
  errorMessage?: string
): boolean =>
  errorCode === ErrorCodes.NumberOfDevicesExceeded ||
  errorMessage?.includes("NumberOfDevicesExceeded") === true

export const createLock = <T>({
  resourcePath,
  successAction,
  failureAction,
  successMessage,
  failureMessage,
  getTokenFromResponse,
}: LockSagaParams<T>) =>
  function* lockSaga(action: AnyAction) {
    const { payload, name } = action as unknown as LockSagaAction

    const { token } = (yield select(getUserData)) as UserData

    const options = {
      credentials: token ? ("same-origin" as const) : ("include" as const),
      customHeaders: token
        ? { Authorization: `Bearer ${token}` }
        : {},
      responseType: "json" as const,
      url: `${resourcePath}/${payload}`,
    }

    try {
      const response = (yield putFetch(options)) as T
      const lockToken = getTokenFromResponse(response)

      yield put(createAction(successAction, lockToken))
      yield put(
        createSnackbarMessage(MessageTypes.INFO, successMessage, { id: name })
      )
    } catch (error: unknown) {
      const err = error as ApiError
      const errorCode = normalizeErrorCode(err)
      const errorMessageText = typeof err?.message === "string" ? err.message : ""
      let messageKey: string

      if (isDeviceLimitError(errorCode, errorMessageText)) {
        messageKey = ErrorCodes.NumberOfDevicesExceeded
      } else if (errorCode && Object.values<string>(ErrorCodes).includes(errorCode)) {
        messageKey = errorCode
      } else {
        messageKey = failureMessage
      }

      yield put(createAction(failureAction))
      yield put(
        createSnackbarMessage(MessageTypes.ERROR, messageKey, { id: name })
      )
    }
  }